import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeft,
  Save,
  Loader2,
  Cpu,
  Hash,
  Building,
  Tag,
  MapPin,
  Calendar,
} from "lucide-react";
import { machinesAPI } from "../api";
import toast from "react-hot-toast";

// Moved outside AddMachine so it isn't recreated on every keystroke
const FormRow = ({ icon: Icon, label, children }) => (
  <div>
    <label className="label mb-2 flex items-center gap-2 font-semibold text-slate-700">
      <Icon className="w-4 h-4 text-primary-500" />
      {label}
    </label>
    {children}
  </div>
);

const AddMachine = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: "",
    model: "",
    manufacturer: "",
    serialNumber: "",
    locationInPlant: "",
    installationDate: new Date().toISOString().split("T")[0],
  });

  const mutation = useMutation({
    mutationFn: (data) => machinesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["machines"] });
      toast.success("Machine added successfully!");
      navigate("/machines");
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || "Failed to add machine");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate(formData);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 pb-12">
      <div className="flex items-center gap-4">
        <Link to="/machines" className="btn btn-ghost btn-circle">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <h1 className="text-2xl font-bold text-slate-900">Add New Machine</h1>
      </div>

      <form
        onSubmit={handleSubmit}
        className="card p-8 space-y-6 bg-white border border-slate-200 shadow-sm"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormRow icon={Cpu} label="Machine Name*">
            <input
              type="text"
              required
              className="input input-field"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g. CNC Miller 01"
            />
          </FormRow>
          <FormRow icon={Tag} label="Model Number">
            <input
              type="text"
              className="input input-field"
              value={formData.model}
              onChange={(e) =>
                setFormData({ ...formData, model: e.target.value })
              }
              placeholder="e.g. MX-4500"
            />
          </FormRow>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormRow icon={Building} label="Manufacturer">
            <input
              type="text"
              className="input input-field"
              value={formData.manufacturer}
              onChange={(e) =>
                setFormData({ ...formData, manufacturer: e.target.value })
              }
              placeholder="e.g. Acme Machines"
            />
          </FormRow>
          <FormRow icon={Hash} label="Serial Number">
            <input
              type="text"
              className="input input-field"
              value={formData.serialNumber}
              onChange={(e) =>
                setFormData({ ...formData, serialNumber: e.target.value })
              }
              placeholder="e.g. SN-12345678"
            />
          </FormRow>
        </div>

        <FormRow icon={MapPin} label="Location in Plant">
          <input
            type="text"
            className="input input-field"
            value={formData.locationInPlant}
            onChange={(e) =>
              setFormData({ ...formData, locationInPlant: e.target.value })
            }
            placeholder="e.g. Bay 3, Section A"
          />
        </FormRow>

        <FormRow icon={Calendar} label="Installation Date">
          <input
            type="date"
            className="input input-field"
            value={formData.installationDate}
            onChange={(e) =>
              setFormData({ ...formData, installationDate: e.target.value })
            }
          />
        </FormRow>

        <div className="flex justify-end gap-3 pt-6 border-t border-slate-100">
          <Link to="/machines" className="btn btn-secondary">
            Cancel
          </Link>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="btn btn-primary shadow-md"
          >
            {mutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Machine
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddMachine;